import Papa from 'papaparse';

const SKIP_KEYS = new Set(['image', 'link', 'repr_link']);

function formatCell(p, colKey, storeName) {
    if (colKey === 'sku') return p.sku || '';
    if (colKey === 'fba') return p.fba ? 'Yes' : 'No';
    if (colKey === 'price_min') return p.price_min != null ? Number(p.price_min).toFixed(2) : '';
    if (colKey === 'price_max') return p.price_max != null ? Number(p.price_max).toFixed(2) : '';
    if (colKey === 'gross_margin') return p.gross_margin != null ? `${(Number(p.gross_margin) * 100).toFixed(1)}%` : '';
    if (colKey === 'doi_7d' || colKey === 'doi_30d' || colKey === 'doi_90d') {
        const days = colKey === 'doi_7d' ? 7 : colKey === 'doi_30d' ? 30 : 90;
        const qty = p.qty_available || 0;
        if (!qty) return '';
        const units = days === 7 ? p.units_7d : days === 30 ? p.units_30d : p.units_90d;
        if (!units || units <= 0) return '999';
        return (qty / (units / days)).toFixed(2);
    }
    const v = colKey === 'why_inactive' ? (p.status === 'Active' ? '' : (p.listing_reason || '')) : p[colKey];
    if (v == null) return '';
    if (typeof v === 'number') {
        // Money-ish columns: 2 decimals; everything else as-is
        const isMoney = /price|fee|revenue/i.test(colKey);
        return isMoney ? v.toFixed(2) : String(v);
    }
    return String(v);
}

/**
 * Serialize the current filtered products view to a CSV string.
 *
 * @param {Array} filteredProducts — rows in the order shown in the table
 * @param {Array} visibleColumns — array of column descriptors {key, label}
 * @param {Function} storeNameFor — (store_id) => display name
 */
export function serializeProducts(filteredProducts, visibleColumns, storeNameFor) {
    const exportableCols = visibleColumns.filter(c => !SKIP_KEYS.has(c.key) && c.key !== 'store_id');
    // Build header row: pinned first, then any visible columns not already pinned
    const visibleKeyToLabel = new Map();
    visibleKeyToLabel.set('sku', 'SKU');
    visibleKeyToLabel.set('price_min', 'Min Price');
    visibleKeyToLabel.set('price_max', 'Max Price');
    for (const c of exportableCols) {
        if (!visibleKeyToLabel.has(c.key)) visibleKeyToLabel.set(c.key, c.label || c.key);
    }
    // Final ordered (key, header-label) list
    const orderedKeys = [];
    const headers = [];
    headers.push('Store'); orderedKeys.push('__store__');
    headers.push('SKU'); orderedKeys.push('sku');
    headers.push('Title'); orderedKeys.push('title');
    headers.push('Min Price'); orderedKeys.push('price_min');
    headers.push('Max Price'); orderedKeys.push('price_max');
    for (const c of exportableCols) {
        if (['sku', 'title', 'price_min', 'price_max', 'store_display_name'].includes(c.key)) continue;
        headers.push(c.label || c.key);
        orderedKeys.push(c.key);
    }

    const data = filteredProducts.map(p => {
        const row = {};
        for (let i = 0; i < orderedKeys.length; i++) {
            const k = orderedKeys[i];
            const h = headers[i];
            if (k === '__store__') row[h] = storeNameFor(p.store_id) || '';
            else row[h] = formatCell(p, k, storeNameFor(p.store_id));
        }
        return row;
    });
    return Papa.unparse({ fields: headers, data: data.map(r => headers.map(h => r[h])) });
}

export function downloadCsv(filename, content) {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

export function exportFilename(storeName) {
    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    const safe = (storeName || 'all').replace(/\s+/g, '-').replace(/[^A-Za-z0-9-]/g, '');
    return `pricing-${safe}-${today}.csv`;
}
